import React, {useEffect, useRef, useState} from "react";
import {PAGE_FRAGMENT_SM_WIDTH, PD_SM, PD_XXLG} from "./dimens";
import {MicOutlined} from "@mui/icons-material";
import {Button} from "@mui/material";
import {StyledBoxColumn} from "./StyledComponents";
import {lightGray} from "./colors";
import {Debouncer} from "./file_util";

class StreamAnimation {

  private stream: MediaStream;
  private canceled: boolean;

  stop(): void {
    this.canceled = true;
  }

  start(canvas: HTMLCanvasElement, stream: MediaStream, silenceDetected?: () => void): void {
    this.stream = stream;
    this.canceled = false;
    const WIDTH = canvas.width;
    const HEIGHT = canvas.height;
    const bufferLength = 2048;
    const dataArray = new Uint8Array(bufferLength);
    const startAt = Date.now();
    const ambientEndAt = Math.floor(startAt + 0.7 * 1000);
    let ambientSum = 0, ambientValue = -1, ambientSampleCount = 0;
    let speakingAt = -1;

    const canvasCtx = canvas.getContext("2d");

    const self = this;

    function draw() {
      if (self.canceled) {
        return;
      }
      requestAnimationFrame(draw);

      analyser.getByteTimeDomainData(dataArray);

      canvasCtx.fillStyle = lightGray;
      canvasCtx.fillRect(0, 0, WIDTH, HEIGHT);

      canvasCtx.lineWidth = 2;
      canvasCtx.strokeStyle = "rgb(0, 0, 0)";

      canvasCtx.beginPath();

      let sliceWidth = (WIDTH * 1.0) / bufferLength;
      let x = 0;

      let max = 0;
      for (let i = 0; i < bufferLength; i++) {
        let v = dataArray[i] / 128.0;
        max = Math.floor(Math.max(max, (v - 1) * 100));
        let y = (v * HEIGHT) / 2;

        if (i === 0) {
          canvasCtx.moveTo(x, y);
        } else {
          canvasCtx.lineTo(x, y);
        }

        x += sliceWidth;
      }

      canvasCtx.lineTo(canvas.width, canvas.height / 2);
      canvasCtx.stroke();

      const now = Date.now();
      if (now < ambientEndAt) {
        ambientSum += max;
        ambientSampleCount++;
      } else {
        if (ambientValue < 0) {
          ambientValue = ambientSum / (ambientSampleCount || 1);
          // console.log("JSG ambient: " + ambientValue);
        } else if (max > (ambientValue * 3 + 16)) {
          speakingAt = now;
          // console.log("JSG speaking at: " + now);
        } else if (speakingAt > 0 && (now - speakingAt) >= 2.5 * 1000) {
          // console.log("JSG silence");
          speakingAt = -1;
          silenceDetected?.();
        }
      }
      // canvasCtx.fillStyle = "black";
      // canvasCtx.font = "16px sans-serif";
      // canvasCtx.fillText(max.toString(), canvas.width / 2, canvas.height / 2);
    }

    const audioCtx = new AudioContext();
    const source = audioCtx.createMediaStreamSource(this.stream);

    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = bufferLength;

    source.connect(analyser);

    draw();
  }
}

const ANIM = new StreamAnimation();

export enum RecordAudioDisplayMode {
  FULL,
  EMBED,
  HANDSFREE,
}

function startRecording(mediaRecorder: MediaRecorder, onDone: (url: string) => void, setRecording: (recording: boolean) => void): void {
  if (mediaRecorder.state === "recording") {
    return;
  }
  let chunks: Blob[] = [];
  mediaRecorder.start();
  mediaRecorder.onstop = function (e) {
    const blob = new Blob(chunks, {type: mediaRecorder.mimeType});
    chunks = [];
    const audioURL = window.URL.createObjectURL(blob);
    onDone(audioURL);
  };

  mediaRecorder.ondataavailable = function (e) {
    chunks.push(e.data);
  };
  setRecording(true);
}

function stopRecording(mediaRecorder: MediaRecorder, setRecording: (recording: boolean) => void): void {
  if (mediaRecorder?.state !== "recording") {
    return;
  }
  mediaRecorder?.stop();
  setRecording(false);
}

const debouncer = new Debouncer(100);

function createMediaRecorder(): Promise<MediaRecorder> {
  const constraints = {audio: true};
  return new Promise((resolve, reject) => {
    navigator.mediaDevices.getUserMedia(constraints)
      .then((value: MediaStream) => {
        resolve(new MediaRecorder(value));
      }, reason => {
        reject(reason);
      });
  });
}

function isSmallDisplayMode(displayMode: RecordAudioDisplayMode): boolean {
  switch (displayMode) {
    case RecordAudioDisplayMode.EMBED:
    case RecordAudioDisplayMode.HANDSFREE:
      return true;
  }
  return false;
}

export function RecordAudioFragment(props: {
  width?: number,
  displayMode?: RecordAudioDisplayMode,
  disabled?: boolean,
  autoStart?: boolean, // Starts as soon as permission is in "granted" state AND fragment is enabled (ie. not disabled).
  autoStop?: boolean, // Detects silence for 2-3 seconds before ending.
  onDone: (url: string) => void,
  onError?: (reason: string) => void,
}) {
  const canvasRef = useRef<HTMLCanvasElement>();
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder>();
  const [recording, setRecording] = useState<boolean>(false);
  const autoStart = props.autoStart || props.displayMode === RecordAudioDisplayMode.HANDSFREE;
  const autoStop = props.autoStop || props.displayMode === RecordAudioDisplayMode.HANDSFREE;

  useEffect(() => {
    if (recording) {
      ANIM.start(canvasRef.current, mediaRecorder.stream, () => {
        if (autoStop) {
          stopRecording(mediaRecorder, setRecording);
        }
      });
    } else {
      ANIM.stop();
    }
  }, [mediaRecorder, recording]);
  useEffect(() => {
    if (!mediaRecorder) {
      return;
    }
    if (!props.disabled && autoStart) {
      startRecording(mediaRecorder, props.onDone, setRecording);
    }
  }, [mediaRecorder]);
  useEffect(() => {
    if (!mediaRecorder) {
      return;
    }
    if (!props.disabled && autoStart) {
      startRecording(mediaRecorder, props.onDone, setRecording);
    }
  }, [props.disabled]);
  useEffect(() => {
    navigator.permissions.query(
      //@ts-ignore
      {name: "microphone"})
      .then(result => {
        if (result.state === "granted") {
          debouncer.run(() => createMediaRecorder().then(mediaRecorder => setMediaRecorder(mediaRecorder)));
        }
      });
    return () => {
      stopRecording(mediaRecorder, setRecording);
      ANIM.stop();
      setMediaRecorder(null);
    }
  }, []);
  const width = props.width || PAGE_FRAGMENT_SM_WIDTH;
  return <StyledBoxColumn
    style={{
      width: "84vw",
      maxWidth: PAGE_FRAGMENT_SM_WIDTH,
      padding: !isSmallDisplayMode(props.displayMode) ? PD_XXLG : PD_SM,
      alignItems: "center",
    }}>
    {!isSmallDisplayMode(props.displayMode) ? <MicOutlined style={{fontSize: 48}}/> : null}
    <Button
      disabled={!mediaRecorder}
      onClick={() => {
        if (!recording) {
          startRecording(mediaRecorder, props.onDone, setRecording);
        } else {
          stopRecording(mediaRecorder, setRecording);
        }
      }}
      style={{padding: 0}}>
      <canvas
        ref={canvasRef}
        style={{background: "white", borderRadius: width / 16, width: width - PD_XXLG * 2, height: width / 8}}/>
    </Button>
    {mediaRecorder
      ? (
        !recording
          ? (props.displayMode !== RecordAudioDisplayMode.HANDSFREE
            ? <Button
              onClick={() => {
                startRecording(mediaRecorder, props.onDone, setRecording);
              }}
              disabled={!mediaRecorder}
              variant="contained">Start</Button>
            : null)
          : (props.displayMode !== RecordAudioDisplayMode.HANDSFREE
            ? <Button
              onClick={() => {
                stopRecording(mediaRecorder, setRecording);
              }}
              disabled={!mediaRecorder}
              variant="contained">Stop</Button>
            : null)
      )
      : <Button
        onClick={() => createMediaRecorder().then(mediaRecorder => setMediaRecorder(mediaRecorder))}
        variant="outlined">
        Grant permission
      </Button>}
  </StyledBoxColumn>;
}